import {
  ReactNode,
  createContext,
  useCallback,
  useMemo,
  useReducer,
} from 'react';

import Customer from '@/shared/types/customer.type';
import QuoteResponse from '@/shared/types/order/quotes/quoteResponse.type';
import ProductOrder from '@/shared/types/productOrder.type';

import cartReducer, { initialState } from '../reducer/cartReducer';

type CartContextType = {
  productsInCart: ProductOrder[];
  assignedCustomer?: Customer;
  assignedQuote?: QuoteResponse;
  onAssignCustomer: (customer: Customer) => void;
  onUnassignCustomer: () => void;
  addProductToCart: (product: ProductOrder) => void;
  updateProduct: (product: ProductOrder) => void;
  removeProductFromCart: (id: string) => void;
  onAssignQuote: (quote: QuoteResponse) => void;
  onUnassignQuote: () => void;
  onUpdateQuote: (quote: QuoteResponse) => void;
  clearCart: () => void;
};

export const CartContext = createContext<CartContextType | undefined>(
  undefined,
);

type CartContextProviderProps = {
  children: ReactNode;
};

const CartContextProvider = ({ children }: CartContextProviderProps) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  const addProductToCart = useCallback((product: ProductOrder) => {
    if (!product) return;
    dispatch({ type: 'ADD_PRODUCT', product });
  }, []);

  const updateProduct = useCallback((product: ProductOrder) => {
    dispatch({ type: 'UPDATE_PRODUCT', product });
  }, []);

  const removeProductFromCart = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_PRODUCT', id });
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR_CART' });
  }, []);

  const onAssignCustomer = useCallback((customer: Customer) => {
    dispatch({ type: 'ASSIGN_CUSTOMER', customer });
  }, []);

  const onUnassignCustomer = useCallback(() => {
    dispatch({ type: 'UNASSIGN_CUSTOMER' });
  }, []);

  const onAssignQuote = useCallback((quote: QuoteResponse) => {
    dispatch({ type: 'ASSIGN_QUOTE', quote });
  }, []);

  const onUnassignQuote = useCallback(() => {
    dispatch({ type: 'UNASSIGN_QUOTE' });
  }, []);

  const onUpdateQuote = useCallback((quote: QuoteResponse) => {
    dispatch({ type: 'UPDATE_QUOTE', quote });
  }, []);

  const memoizedCartState = useMemo(
    () => ({
      productsInCart: state.productsInCart,
      assignedCustomer: state.assignedCustomer,
      assignedQuote: state.assignedQuote,
      onAssignCustomer,
      onUnassignCustomer,
      addProductToCart,
      updateProduct,
      removeProductFromCart,
      onAssignQuote,
      onUnassignQuote,
      onUpdateQuote,
      clearCart,
    }),
    [
      addProductToCart,
      clearCart,
      onAssignCustomer,
      onAssignQuote,
      onUnassignQuote,
      onUnassignCustomer,
      onUpdateQuote,
      removeProductFromCart,
      state.assignedCustomer,
      state.assignedQuote,
      state.productsInCart,
      updateProduct,
    ],
  );

  return (
    <CartContext.Provider value={memoizedCartState}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContextProvider;
